import {Injectable} from '@angular/core';
import {Observable} from 'rxjs';
import {ApiResponse} from '../classes/api-response';
import {environment} from '../../environments/environment';
import {map} from 'rxjs/operators';
import {Testbed} from '../model/testbed';
import {HttpClient, HttpParams} from '@angular/common/http';
import {Constants} from "../utils/constants";
import {AuthService} from "./auth.service";
import {StorageFile} from '../model/storageFile';
import {hasPermission, User} from "../model/user";

@Injectable({
  providedIn: 'root'
})
export class TestbedsService {

  private readonly entities = Constants.entities;
  private readonly baseUrl = `${environment.apiUrl}/${this.entities.testbeds}`;
  private readonly loggedUser: User;
  private readonly iAmCustomer: boolean;
  private readonly iAmAdmin: boolean;


  constructor(private http: HttpClient, private authService: AuthService) {
    this.loggedUser = this.authService.loggedUser();
    this.iAmCustomer = hasPermission(this.loggedUser, "customer");
    this.iAmAdmin = hasPermission(this.loggedUser, "admin");
  }

  public getAllTestbeds(): Observable<Testbed[]> {
    return this.http
      .get<ApiResponse>(`${this.baseUrl}`)
      .pipe(map(value => value.data as Testbed[]));
  }

  public getAllTestbedsByCompany(companyId?: number, tests?: boolean): Observable<Testbed[]> {
    const queryTests = tests ? '?tests' : '';
    return this.http
      .get<ApiResponse>(`${environment.apiUrl}/${this.entities.companies}/${companyId}/${this.entities.testbeds}${queryTests}`)
      .pipe(map(value => value.data as Testbed[]));
  }

  // public getAllTestbedsByTester(testerId?: number): Observable<Testbed[]> {
  //   return this.http
  //     .get<ApiResponse>(`${environment.apiUrl}/${this.entities.testers}/${testerId}/${this.entities.testbeds}`)
  //     .pipe(map(value => value.data as Testbed[]));
  // }

  public getAllTestbedsByTester(testerId?: number): Observable<Testbed[]> {
    return this.http
      .get<ApiResponse>(`${environment.apiUrl}/${this.entities.testers}/${testerId}/${this.entities.testbeds}`)
      .pipe(map(value => value.data.map(item => item.testbed) as Testbed[]));
  }

  public getTestbedById(testbedId?: number | string, tests?: boolean): Observable<Testbed> {
    const queryTests = tests ? '?tests' : '';
    return this.http
      .get<ApiResponse>(`${this.baseUrl}/${testbedId}${queryTests}`,)
      .pipe(map(value => value.data as Testbed));
  }

  public createTestbed(testbed: Testbed): Observable<Testbed> {

    return (this.iAmCustomer && !this.iAmAdmin) ?
      this.http
        .post<ApiResponse>(`${environment.apiUrl}/${this.entities.companies}/${this.loggedUser?.company?.id}/${this.entities.testbeds}`, testbed)
        .pipe(map(value => value.data as Testbed)) :
      this.http
        .post<ApiResponse>(`${this.baseUrl}`, testbed)
        .pipe(map(value => value.data as Testbed));
  }

  public updateTestbed(testbed: Testbed): Observable<void> {
    return (this.iAmCustomer && !this.iAmAdmin) ?
      this.http
        .patch<ApiResponse>(`${environment.apiUrl}/${this.entities.companies}/${this.loggedUser?.company?.id}/${this.entities.testbeds}/${testbed.id}`, testbed)
        .pipe(map(() => {
        })) :
      this.http
        .patch<ApiResponse>(`${this.baseUrl}/${testbed.id}`, testbed)
        .pipe(map(() => {
        }));
  }

  public deleteTestbed(id: number): Observable<void> {
    return this.http
      .delete<ApiResponse>(`${this.baseUrl}/${id}`)
      .pipe(map(() => {
      }));
  }

  public associateTestbed(companyId?: number, testbed?: Testbed, contractId?: number): Observable<void> {
    return this.http
      .put<ApiResponse>(`${environment.apiUrl}/${this.entities.contracts}/${contractId}/${this.entities.testbed}`, testbed)
      .pipe(map(() => {
      }));
  }

  public getTestbedFiles(testbed: Testbed): Observable<StorageFile[]> {
    return this.http
      .get<ApiResponse>(`${this.baseUrl}/${testbed.id}/files`)
      .pipe(map(value => value.data as StorageFile[]));
  }


  public downloadFile(url: string): Observable<any> {

    return this.http.get(
      url,
      {
        responseType: 'blob',
      });
  }

  public getSignedUrlForDownload(filename: string, testbedId?: number): Observable<any> {

    let params: HttpParams = new HttpParams();
    params = params.append('filename', filename);
    return this.http
      .get(`${this.baseUrl}/${testbedId}/files/downloadSignedUrl`, {params})
      .pipe(map(value => value as any));
  }

  public getSignedUrlForClient(operatingSystem: string, version: string): Observable<any> {
    return this.http
      .get(`${environment.apiUrl}/files/weseth_client/${operatingSystem}/${version}`)
      .pipe(map(value => value as any));
  }


  public getSignedUrlForUpload(filename: string, byteLength: number, testbedId?: number): Observable<any> {

    let params: HttpParams = new HttpParams();
    params = params.append('filename', filename);
    params = params.append('byteLength', byteLength);

    return this.http
      .get<ApiResponse>(`${this.baseUrl}/${testbedId}/files/uploadSignedUrl`, {params: params})
      .pipe(
        map(response => response as any));

  }

  public uploadFile(arrayBuffer: ArrayBuffer, filename: string, fileType: string, url: string, fields: {
    [key: string]: string
  }): Observable<any> {

    const formData = this.buildFormData(filename, url, fields);

    formData.set("key", filename);
    formData.set("file", new Blob([arrayBuffer], {type: fileType}), filename);

    return this.http.post(
      url,
      formData,
      {
        reportProgress: true,
        observe: 'events'
      });

  }

  buildFormData(filename: string, url: string, fields: { [p: string]: string }): FormData {
    const formData = new FormData();
    formData.set("key", filename);

    if (url && fields) {
      for (const field in fields) {
        formData.set(field, fields[field]);
      }
    }
    return formData;

  }


  deleteFile(testbed: Testbed, file: StorageFile): Observable<void> {
    return this.http
      .delete<ApiResponse>(`${this.baseUrl}/${testbed.id}/files/${file.name}`)
      .pipe(map(() => {
      }));
  }

}
